import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import LandsTab from "./properties/LandsTab";
import PropertiesTab from "./properties/PropertiesTab";
import UnitsTab from "./properties/UnitsTab";
import TreeTab from "./properties/TreeTab";
import ByInstituteTab from "./properties/ByInstituteTab";
import PropertyDashboard from "./properties/PropertyDashboard";

const TABS = [
  { v: "dashboard", l: "Dashboard" },
  { v: "lands", l: "Lands" },
  { v: "properties", l: "Properties" },
  { v: "units", l: "Units" },
  { v: "tree", l: "Tree View" },
  { v: "byinstitute", l: "By Institute" },
];

export default function Properties() {
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite("immovables") : true;

  const [lands, setLands] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [tree, setTree] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [l, p, u, a, i, t, s] = await Promise.all([
        api.get("/lands"),
        api.get("/properties"),
        api.get("/units"),
        api.get("/allocations"),
        api.get("/institutes"),
        api.get("/property-tree"),
        api.get("/property-summary"),
      ]);
      setLands(l.data);
      setProperties(p.data);
      setUnits(u.data);
      setAllocations(a.data);
      setInstitutes(i.data);
      setTree(t.data?.tree || []);
      setSummary(s.data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load",
      );
    }
  };

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="properties-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
            Trust Estate
          </div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Properties</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Manage lands, buildings, units and their movable / immovable items in one place. Use the
            dashboard for a quick health check.
          </p>
        </div>
      </div>

      {!writable && (
        <div
          data-testid="properties-readonly-banner"
          className="mb-6 px-4 py-3 border border-[#d4af37]/40 bg-[#d4af37]/5 rounded-sm text-sm text-[#8a6d1d]"
        >
          You have read-only access to Properties.
        </div>
      )}

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : (
        <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "dashboard"}>
          <TabsList className="bg-transparent p-0 border-b border-[#e5e0d8] rounded-none h-auto w-full justify-start gap-4 flex-wrap mb-6">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                data-testid={`tab-${t.v}`}
                className="data-[state=active]:bg-transparent data-[state=active]:text-[#234333] data-[state=active]:border-b-2 data-[state=active]:border-[#234333] rounded-none px-0 py-3 text-sm font-sans text-[#6b655b] shadow-none"
              >
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="dashboard">
            <PropertyDashboard summary={summary} />
          </TabsContent>
          <TabsContent value="lands">
            <LandsTab lands={lands} refresh={refresh} writable={writable} />
          </TabsContent>
          <TabsContent value="properties">
            <PropertiesTab
              properties={properties}
              lands={lands}
              refresh={refresh}
              writable={writable}
            />
          </TabsContent>
          <TabsContent value="units">
            <UnitsTab
              units={units}
              properties={properties}
              lands={lands}
              refresh={refresh}
              writable={writable}
            />
          </TabsContent>
          <TabsContent value="tree">
            <TreeTab tree={tree} />
          </TabsContent>
          <TabsContent value="byinstitute">
            <ByInstituteTab
              institutes={institutes}
              allocations={allocations}
              lands={lands}
              properties={properties}
              units={units}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
